<template>
  <div class="load-board">
    <div class="load-legend">
      <span class="swatch swatch-ok" /> A tiempo
      <span class="swatch swatch-risk" /> Entrega el mismo día
      <span class="swatch swatch-late" /> Atrasada
      <span class="legend-note">Arrastra una OT a la máquina del área, o usa el menú del bloque. El ancho es la carga en esa máquina.</span>
    </div>

    <div v-if="!machines.length" class="load-empty">
      <v-icon size="40" color="primary" class="mb-3">fas fa-industry</v-icon>
      <div class="text-h6 font-weight-bold primary--text">No hay áreas habilitadas</div>
    </div>

    <div v-else class="load-table" @scroll="hideHover">
      <div class="load-head sticky">
        <div class="load-head-machine">Área / máquina</div>
        <div class="load-head-track">
          <div
            v-for="col in dayColumns"
            :key="col.key"
            class="load-day"
          >
            <span class="load-day-name">{{ col.label }}</span>
            <span class="load-day-date">{{ col.sub }}</span>
          </div>
        </div>
      </div>

      <div
        v-for="group in groups"
        :key="'area-' + group.machine.id"
        class="area-group"
      >
        <div class="area-header">
          <div class="area-title">{{ group.machine.name }}</div>
          <div class="area-meta">
            {{ group.pendingCount }} OT · {{ group.daysOfQueue }} {{ group.daysOfQueue === 1 ? 'día' : 'días' }} de cola
            <span v-if="group.lateCount" class="late-count"> · {{ group.lateCount }} atrasadas</span>
          </div>
        </div>

        <div
          v-for="lane in group.lanes"
          :key="lane.key"
          class="load-row"
          :class="{
            'is-drop-target': isDropTarget(group.machine.id, lane.equipmentId),
            'is-unassigned': lane.equipmentId === null
          }"
          :data-machine-id="group.machine.id"
          :data-equipment-id="lane.equipmentId == null ? '' : lane.equipmentId"
        >
          <div class="load-machine">
            <div class="load-machine-name">{{ lane.name }}</div>
            <div class="load-machine-days">
              {{ lane.daysOfQueue }} {{ lane.daysOfQueue === 1 ? 'día' : 'días' }}
            </div>
            <div class="load-machine-meta">{{ lane.pendingCount }} OT</div>
            <div class="load-meter">
              <div class="load-meter-fill" :style="{ width: lane.windowFill + '%' }" />
            </div>
          </div>

          <div class="load-track">
            <div class="load-day-grid">
              <div v-for="col in dayColumns" :key="'g-' + lane.key + '-' + col.key" class="load-day-cell" />
            </div>

            <div v-if="!lane.visible.length" class="load-idle">
              {{ lane.equipmentId === null ? 'Sin OT por asignar' : 'Sin carga en esta ventana' }}
            </div>

            <div v-else class="load-strip">
              <div
                v-for="item in lane.visible"
                :key="item.id"
                class="load-bar-wrap"
                :style="{ flex: '0 0 ' + barBasis(item, lane) }"
                @mouseenter="showHover($event, item, group.machine)"
                @mouseleave="hideHover"
              >
                <button
                  type="button"
                  class="load-bar"
                  :class="barClass(item)"
                  @mousedown.prevent="onBarDown($event, group.machine, item, lane.equipmentId)"
                  @click="onBarClick(item)"
                >
                  <v-icon v-if="item.reprocess" x-small color="white">fas fa-redo</v-icon>
                  <span v-if="showOt(item, lane)" class="load-ot">OT {{ item.work_order }}</span>
                  <span v-if="showProc(item, lane)" class="load-proc">{{ item.name }}</span>
                </button>
                <v-menu v-if="group.equipments.length" offset-y>
                  <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                    <button
                      v-bind="menuAttrs"
                      v-on="menuOn"
                      type="button"
                      class="assign-hit"
                      @mousedown.stop
                      @click.stop
                    >
                      <v-icon x-small>fas fa-chevron-down</v-icon>
                    </button>
                  </template>
                  <v-list dense>
                    <v-list-item @click="assignItem(item, group.machine, null)">
                      <v-list-item-title>Sin asignar</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-for="eq in group.equipments"
                      :key="eq.id"
                      @click="assignItem(item, group.machine, eq)"
                    >
                      <v-list-item-title>{{ eq.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>

            <div v-if="lane.overflowCount" class="load-overflow">
              +{{ lane.overflowCount }} más
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="drag && drag.moved" class="load-ghost" :style="ghostStyle">
      OT {{ drag.item.work_order }} · {{ drag.item.name }}
    </div>

    <div
      v-if="hover && hover.item && !(drag && drag.moved)"
      ref="hoverCard"
      class="ot-hover"
      :style="hoverStyle"
    >
      <div class="ot-hover-ot">OT {{ hover.item.work_order }}</div>
      <div class="ot-hover-row">
        <span>Cliente</span>
        <strong>{{ hover.item.customer_name || '—' }}</strong>
      </div>
      <div class="ot-hover-row">
        <span>Producto</span>
        <strong>{{ hover.item.product_name || '—' }}</strong>
      </div>
      <div v-if="hover.item.product_sku" class="ot-hover-row">
        <span>SKU</span>
        <strong>{{ hover.item.product_sku }}</strong>
      </div>
      <div class="ot-hover-row">
        <span>Proceso</span>
        <strong>{{ hover.item.name || '—' }}</strong>
      </div>
      <div class="ot-hover-meta">
        {{ remainingLabel(hover.item, hover.machine) }}
        · {{ hoursLabel(hover.item) }}
        · {{ formatDay(hover.item.delivery_date) }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  matchesSearch,
  matchesRisk,
  matchesEquipment,
  remainingWork,
} from '@/utils/plannerEstimate'

export default {
  name: 'PlantGantt',
  props: {
    machines: { type: Array, default: () => [] },
    search: { type: String, default: '' },
    riskFilter: { type: String, default: 'all' },
    equipmentFilter: { type: String, default: 'Todos' },
    zoom: { type: Number, default: 7 },
  },
  data() {
    return {
      drag: null,
      suppressClick: false,
      hover: null,
    }
  },
  computed: {
    dayColumns() {
      const cols = []
      if (this.zoom === 1) {
        for (let hour = 8; hour < 20; hour += 2) {
          cols.push({
            key: 'h' + hour,
            label: String(hour).padStart(2, '0') + ':00',
            sub: hour < 12 ? 'mañana' : 'tarde',
          })
        }
        return cols
      }
      const cursor = new Date()
      cursor.setHours(0, 0, 0, 0)
      for (let i = 0; i < this.zoom; i++) {
        const d = new Date(cursor)
        d.setDate(cursor.getDate() + i)
        cols.push({
          key: d.toISOString().slice(0, 10),
          label: d.toLocaleDateString('es-CL', { weekday: 'short' }).replace('.', ''),
          sub: d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' }),
        })
      }
      return cols
    },
    groups() {
      return this.machines.map((machine) => this.buildGroup(machine))
    },
    hasSearch() {
      return !!(this.search && String(this.search).trim())
    },
    ghostStyle() {
      if (!this.drag) return {}
      return { left: this.drag.x + 12 + 'px', top: this.drag.y + 12 + 'px' }
    },
    hoverStyle() {
      if (!this.hover) return {}
      return {
        left: this.hover.left + 'px',
        top: this.hover.top + 'px',
      }
    },
  },
  mounted() {
    this._onMove = this.onPointerMove.bind(this)
    this._onUp = this.onPointerUp.bind(this)
  },
  beforeDestroy() {
    this.unbindPointer()
  },
  methods: {
    windowHours(machine) {
      const perDay = Math.max(1, Number(machine.hours_per_day) || 8)
      if (this.zoom === 1) return perDay
      let hours = 0
      const cursor = new Date()
      cursor.setHours(8, 0, 0, 0)
      for (let i = 0; i < this.zoom; i++) {
        const day = cursor.getDay()
        const offSat = day === 6 && !machine.saturday
        const offSun = day === 0 && !machine.sunday
        if (!offSat && !offSun) hours += perDay
        cursor.setDate(cursor.getDate() + 1)
      }
      return hours || perDay
    },
    pendingItems(machine) {
      return (machine.queue || []).filter((item) => {
        if (item.done) return false
        if (!matchesEquipment(item, this.equipmentFilter)) return false
        if (!matchesRisk(item, this.riskFilter)) return false
        return true
      })
    },
    enabledEquipments(machine) {
      return (machine.equipments || []).filter((eq) => eq && eq.enabled !== false)
    },
    sliceVisible(items, windowHours) {
      const visible = []
      let used = 0
      items.forEach((item) => {
        if (used >= windowHours) return
        visible.push(item)
        used += Number(item.estimated_hours) || 0
      })
      return visible
    },
    buildLane(machine, items, windowHours, perDay, meta) {
      const totalHours = items.reduce((sum, item) => sum + (Number(item.estimated_hours) || 0), 0)
      const visible = this.sliceVisible(items, windowHours)
      return {
        ...meta,
        machine,
        items,
        visible,
        windowHours,
        overflowCount: Math.max(0, items.length - visible.length),
        pendingCount: items.length,
        daysOfQueue: Math.ceil(totalHours / perDay) || 0,
        windowFill: Math.min(100, Math.round((totalHours / windowHours) * 100) || 0),
      }
    },
    buildGroup(machine) {
      const pending = this.pendingItems(machine)
      const windowHours = this.windowHours(machine)
      const perDay = Math.max(1, Number(machine.hours_per_day) || 8)
      const equipments = this.enabledEquipments(machine)
      const totalHours = pending.reduce((sum, item) => sum + (Number(item.estimated_hours) || 0), 0)
      const lanes = []

      if (equipments.length) {
        lanes.push(this.buildLane(machine, pending.filter((item) => !item.equipment_assigned), windowHours, perDay, {
          key: 'unassigned-' + machine.id,
          name: 'Sin asignar',
          equipmentId: null,
        }))
        equipments.forEach((eq) => {
          lanes.push(this.buildLane(machine, pending.filter((item) => item.equipment_assigned === eq.name), windowHours, perDay, {
            key: 'eq-' + eq.id,
            name: eq.name,
            equipmentId: eq.id,
          }))
        })
      } else {
        lanes.push(this.buildLane(machine, pending, windowHours, perDay, {
          key: 'all-' + machine.id,
          name: 'Cola del área',
          equipmentId: undefined,
        }))
      }

      return {
        machine,
        equipments,
        lanes,
        pendingCount: pending.length,
        lateCount: pending.filter((item) => item.risk === 'late').length,
        daysOfQueue: Math.ceil(totalHours / perDay) || 0,
      }
    },
    isDropTarget(machineId, equipmentId) {
      if (!this.drag) return false
      return this.drag.overMachineId === machineId && this.sameEquipment(this.drag.overEquipmentId, equipmentId)
    },
    sameEquipment(a, b) {
      if (a == null && b == null) return true
      return Number(a) === Number(b)
    },
    showHover(event, item, machine) {
      if (this.drag && this.drag.moved) return
      const wrap = event.currentTarget
      if (!wrap || !wrap.getBoundingClientRect) return
      const rect = wrap.getBoundingClientRect()
      const width = 280
      const height = 150
      let left = rect.left
      left = Math.min(Math.max(12, left), window.innerWidth - width - 12)
      let top = rect.top - height - 10
      if (top < 8) top = rect.bottom + 10
      this.hover = {
        item,
        machine,
        left: Math.round(left),
        top: Math.round(top),
        anchor: rect,
      }
      this.$nextTick(this.placeHover)
    },
    placeHover() {
      const hover = this.hover
      const card = this.$refs.hoverCard
      if (!hover || !hover.anchor || !card) return
      const rect = hover.anchor
      const width = card.offsetWidth || 280
      const height = card.offsetHeight || 150
      let left = rect.left + Math.min(rect.width, 24) - 8
      left = Math.min(Math.max(12, left), window.innerWidth - width - 12)
      let top = rect.top - height - 10
      if (top < 8) top = rect.bottom + 10
      if (top + height > window.innerHeight - 8) {
        top = Math.max(8, window.innerHeight - height - 8)
      }
      hover.left = Math.round(left)
      hover.top = Math.round(top)
    },
    hideHover() {
      this.hover = null
    },
    assignItem(item, machine, equipment) {
      this.$emit('assign-equipment', {
        process: item,
        machineId: machine.id,
        equipmentId: equipment ? equipment.id : null,
        equipmentName: equipment ? equipment.name : '',
      })
    },
    barBasis(item, row) {
      const hours = Math.max(0.25, Number(item.estimated_hours) || 0.25)
      const pct = Math.max(1.2, (hours / row.windowHours) * 100)
      return pct + '%'
    },
    showOt(item, row) {
      const hours = Number(item.estimated_hours) || 0
      return hours >= 1.5 || (hours / row.windowHours) >= 0.08
    },
    showProc(item, row) {
      const hours = Number(item.estimated_hours) || 0
      return hours >= 5 || (hours / row.windowHours) >= 0.16
    },
    barClass(item) {
      return {
        'is-late': item.risk === 'late',
        'is-risk': item.risk === 'risk',
        'is-ok': item.risk === 'ok' || !item.risk,
        'is-match': this.hasSearch && matchesSearch(item, this.search),
        'is-dim': this.hasSearch && !matchesSearch(item, this.search),
        'is-dragging': this.drag && this.drag.item && this.drag.item.id === item.id,
      }
    },
    remainingLabel(item, machine) {
      const remaining = remainingWork(item, machine.production_unit_m2)
      if (machine.production_unit_m2) return remaining.toFixed(1) + ' m²'
      return Math.round(remaining) + ' un'
    },
    hoursLabel(item) {
      const h = Number(item.estimated_hours) || 0
      if (h < 1) return Math.round(h * 60) + ' min'
      return h.toFixed(1) + ' h'
    },
    formatDay(value) {
      if (!value) return 's/fecha'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return 's/fecha'
      return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })
    },
    onBarClick(item) {
      if (this.suppressClick) {
        this.suppressClick = false
        return
      }
      this.$emit('open-work-order', item)
    },
    onBarDown(event, machine, item, equipmentId) {
      this.hideHover()
      this.drag = {
        item,
        fromMachineId: machine.id,
        fromEquipmentId: equipmentId,
        overMachineId: machine.id,
        overEquipmentId: equipmentId,
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
        moved: false,
      }
      window.addEventListener('mousemove', this._onMove)
      window.addEventListener('mouseup', this._onUp)
    },
    onPointerMove(event) {
      if (!this.drag) return
      if (Math.abs(event.clientX - this.drag.startX) + Math.abs(event.clientY - this.drag.startY) > 4) {
        this.drag.moved = true
      }
      this.drag.x = event.clientX
      this.drag.y = event.clientY
      const el = document.elementFromPoint(event.clientX, event.clientY)
      const row = el && el.closest ? el.closest('.load-row') : null
      if (row) {
        this.drag.overMachineId = Number(row.getAttribute('data-machine-id'))
        const raw = row.getAttribute('data-equipment-id')
        this.drag.overEquipmentId = raw === '' || raw == null ? null : Number(raw)
      }
    },
    onPointerUp(event) {
      const drag = this.drag
      this.unbindPointer()
      if (!drag) return
      if (!drag.moved) {
        this.drag = null
        return
      }
      this.suppressClick = true
      const toMachine = this.machines.find((m) => m.id === drag.overMachineId) || this.machines.find((m) => m.id === drag.fromMachineId)
      const fromMachine = this.machines.find((m) => m.id === drag.fromMachineId)
      if (!toMachine || !fromMachine) {
        this.drag = null
        return
      }
      if (fromMachine.id !== toMachine.id) {
        this.$emit('move-process', {
          processId: drag.item.process,
          fromMachineId: fromMachine.id,
          toMachineId: toMachine.id,
          insertAt: this.indexFromClientX(event.clientX, toMachine, drag.overEquipmentId),
        })
      }
      if (drag.overEquipmentId !== undefined && !this.sameEquipment(drag.fromEquipmentId, drag.overEquipmentId)) {
        const equipment = (toMachine.equipments || []).find((eq) => eq.id === drag.overEquipmentId) || null
        this.assignItem(drag.item, toMachine, equipment)
      }
      this.drag = null
    },
    indexFromClientX(clientX, machine, equipmentId) {
      const group = this.groups.find((g) => g.machine.id === machine.id)
      const lane = group && group.lanes.find((l) => this.sameEquipment(l.equipmentId, equipmentId))
      const pending = lane ? lane.items : (machine.queue || [])
      const rows = this.$el.querySelectorAll('.load-row[data-machine-id="' + machine.id + '"]')
      let track = null
      rows.forEach((row) => {
        const raw = row.getAttribute('data-equipment-id')
        const id = raw === '' || raw == null ? null : Number(raw)
        if (this.sameEquipment(id, equipmentId)) {
          track = row.querySelector('.load-strip')
        }
      })
      if (!track || !pending.length || !lane) return pending.length
      const bars = Array.from(track.querySelectorAll('.load-bar'))
      for (let i = 0; i < bars.length; i++) {
        const rect = bars[i].getBoundingClientRect()
        if (clientX < rect.left + rect.width / 2) {
          const processId = lane.visible[i] && lane.visible[i].process
          const idx = pending.findIndex((item) => item.process === processId)
          return idx < 0 ? pending.length : idx
        }
      }
      return pending.length
    },
    unbindPointer() {
      window.removeEventListener('mousemove', this._onMove)
      window.removeEventListener('mouseup', this._onUp)
    },
  },
  watch: {
    search(value) {
      if (!value || !String(value).trim()) return
      this.$nextTick(() => {
        const matches = this.$el.querySelectorAll('.load-bar.is-match')
        if (matches.length === 1) {
          matches[0].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        }
      })
    },
  },
}
</script>

<style scoped>
.load-board {
  background: #fff;
  border: 1px solid rgba(26, 31, 46, 0.08);
  border-radius: 12px;
  overflow: hidden;
}
.load-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(26, 31, 46, 0.08);
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  background: #f8fafc;
}
.legend-note {
  margin-left: auto;
  font-weight: 600;
  color: #64748b;
  font-size: 11px;
}
.swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
  margin-right: 4px;
}
.swatch-ok { background: #0f766e; }
.swatch-risk { background: #c2410c; }
.swatch-late { background: #b91c1c; }
.load-empty { text-align: center; padding: 64px 24px; }
.load-table { max-height: calc(100vh - 300px); overflow: auto; }
.load-head,
.load-row {
  display: grid;
  grid-template-columns: 236px 1fr;
  min-width: 720px;
}
.load-head {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #fff;
  border-bottom: 1px solid rgba(26, 31, 46, 0.1);
}
.load-head-machine {
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  border-right: 1px solid rgba(26, 31, 46, 0.08);
}
.load-head-track {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
}
.load-day {
  padding: 8px 6px;
  text-align: center;
  border-left: 1px solid rgba(26, 31, 46, 0.06);
}
.load-day-name {
  display: block;
  font-size: 11px;
  font-weight: 800;
  color: #1a1f2e;
  text-transform: capitalize;
}
.load-day-date {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}
.load-row { border-bottom: 1px solid rgba(26, 31, 46, 0.06); }
.area-group { border-bottom: 1px solid rgba(26, 31, 46, 0.1); }
.area-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 10px 16px 6px;
  background: #f8fafc;
  border-bottom: 1px solid rgba(26, 31, 46, 0.06);
}
.area-title {
  font-size: 12px;
  font-weight: 800;
  color: #1a1f2e;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.area-meta {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}
.load-row.is-unassigned .load-machine-name { color: #64748b; }
.load-bar-wrap {
  display: flex;
  align-items: stretch;
  min-width: 10px;
  max-width: 100%;
}
.load-bar-wrap .load-bar { flex: 1 1 auto; min-width: 0; border-radius: 3px 0 0 3px; }
.assign-hit {
  flex: 0 0 18px;
  border: 0;
  background: rgba(26, 31, 46, 0.18);
  color: #fff;
  cursor: pointer;
  border-radius: 0 3px 3px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.assign-hit:hover { background: rgba(26, 31, 46, 0.32); }
.load-row.is-drop-target .load-track { background: rgba(53, 189, 200, 0.08); }
.load-machine {
  position: sticky;
  left: 0;
  z-index: 2;
  padding: 12px 16px;
  background: #fff;
  border-right: 1px solid rgba(26, 31, 46, 0.08);
}
.load-machine-name {
  font-size: 12px;
  font-weight: 800;
  color: #1a1f2e;
  line-height: 1.25;
  letter-spacing: -0.01em;
}
.load-machine-days {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 800;
  color: #0f766e;
}
.load-machine-meta {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}
.late-count { color: #b91c1c; }
.load-meter {
  margin-top: 8px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}
.load-meter-fill {
  height: 100%;
  background: #35bdc8;
}
.load-track {
  position: relative;
  min-height: 76px;
  padding: 16px 10px 16px 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.load-day-grid {
  position: absolute;
  inset: 0;
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  pointer-events: none;
}
.load-day-cell {
  border-left: 1px solid rgba(26, 31, 46, 0.05);
}
.load-strip {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  gap: 3px;
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  overflow: hidden;
  flex-wrap: nowrap;
}
.load-bar {
  min-width: 10px;
  box-sizing: border-box;
  border: 0;
  border-radius: 3px;
  color: #fff;
  cursor: grab;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  overflow: hidden;
  white-space: nowrap;
  text-align: left;
  line-height: 1;
}
.load-bar:active { cursor: grabbing; }
.load-bar:focus { outline: 2px solid #35bdc8; outline-offset: 1px; }
.load-bar.is-ok { background: #0f766e; }
.load-bar.is-risk { background: #c2410c; }
.load-bar.is-late { background: #b91c1c; }
.load-bar.is-match { box-shadow: inset 0 0 0 2px #fff; }
.load-bar.is-dim { opacity: 0.18; }
.load-bar.is-dragging { opacity: 0.4; }
.load-ot {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.01em;
}
.load-proc {
  font-size: 11px;
  font-weight: 600;
  opacity: 0.9;
  overflow: hidden;
  text-overflow: ellipsis;
}
.load-redo { font-size: 11px; font-weight: 800; }
.load-overflow {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 800;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 3px;
  padding: 0 10px;
  height: 44px;
  display: flex;
  align-items: center;
  white-space: nowrap;
}
.load-idle {
  position: relative;
  z-index: 1;
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  padding-left: 4px;
}
.load-ghost {
  position: fixed;
  z-index: 40;
  pointer-events: none;
  background: #1a1f2e;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 12px;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(26, 31, 46, 0.25);
}
.ot-hover {
  position: fixed;
  z-index: 50;
  pointer-events: none;
  width: 280px;
  background: #1a1f2e;
  color: #fff;
  padding: 10px 12px;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(26, 31, 46, 0.32);
}
.ot-hover-ot {
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin-bottom: 8px;
}
.ot-hover-row {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 8px;
  margin-top: 3px;
  align-items: start;
}
.ot-hover-row span {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #94a3b8;
  padding-top: 2px;
}
.ot-hover-row strong {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
  color: #fff;
}
.ot-hover-meta {
  margin-top: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #cbd5e1;
}
</style>
